import React, { useState, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import moment from 'moment';
import { Dialog, Transition } from '@headlessui/react';
import {
  Button,
  Icon,
  InputField,
  TextField,
  DateField,
  SelectField,
  validations,
} from '@unite-us/app-components';
import { Spinner } from 'common/spinners';
import SearchableSelectField from 'common/form/SearchableSelectField';
import callOrLog from 'common/utils/callOrLog';
import { TASKS } from 'common/utils/EventTracker/utils/eventConstants';
import { useFind } from 'src/api/APIHooks';
import { getDueTimeInMinutes, timeOfDayOptions } from './taskTimeUtils';
import { useCreateTask } from './tasksQueries';
import { notifyTaskCreatedSuccess, notifyTaskCreatedError } from './taskNotifications';

const EMPTY_FORM = {
  name: '',
  details: '',
  due_date: null,
  due_time: '',
  client_id: '',
  assign_to: '',
};

const NewTaskModalHeader = ({ onClose }) => (
  <Dialog.Title
    as="div"
    className="bg-white px-6 py-4 flex justify-between rounded"
  >
    <h3 className="text-lg font-extrabold leading-6 text-dark-blue">
      Create Task
    </h3>
    <Icon
      icon="Cross"
      size={12}
      color="dark-blue"
      onClick={onClose}
      className="stroke-current stroke-1 cursor-pointer"
    />
  </Dialog.Title>
);

const NewTaskModalLoadingState = () => (
  <div className="flex items-center justify-center py-10" role="alert" aria-live="polite">
    <Spinner />
  </div>
);

const formatDateOfBirth = (dateOfBirth) => {
  if (!dateOfBirth) return '';

  if (typeof dateOfBirth === 'number') {
    return moment.unix(dateOfBirth).utc().format('MM/DD/YYYY');
  }

  return moment.utc(dateOfBirth).format('MM/DD/YYYY');
};

const NewTaskModalFields = ({
  datePicker,
  datePickerRef,
  employeeOptions,
  employeeSearchQuery,
  clientOptions,
  clientSearchQuery,
  form,
  handleChange,
  handleClientInputChange,
  handleClientSelectionChange,
  handleEmployeeInputChange,
  handleEmployeeSelectionChange,
  handleDateChange,
  handleNameBlur,
  loadingEmployees,
  loadingPersons,
  nameState,
  timeOptions,
}) => (
  <div className="input-modal-content">
    <div>
      <InputField
        id="task_name"
        label="Task Name"
        placeholder="Enter Task Name..."
        autoComplete="off"
        required
        validations={validations.isRequired}
        className="mb-2"
        onChange={(e) => handleChange('name', e.target.value)}
        onBlur={handleNameBlur}
        touched={nameState.touched}
        invalid={nameState.invalid}
        error={nameState.error}
        maxLength={60}
      />
    </div>
    <div>
      <TextField
        id="task_details"
        placeholder="Enter Task Details..."
        className="input-textarea"
        label="Task Details"
        required
        onChange={(e) => handleChange('details', e.target.value)}
        maxLength={500}
      />
    </div>
    <div className="flex justify-between">
      <div className="new-task-modal__datefield mr-0 w-1/2 pr-5">
        <DateField
          ref={datePickerRef}
          id="task_remind_at"
          label="Task Due Date"
          required
          className="w-full border-solid"
          onChange={handleDateChange}
          valueFormat="MM/DD/YYYY"
          minDate={new Date(Date.now() - 86400000)}
          isUtc={false}
          inline={false}
        />
      </div>
      <SelectField
        id="task_remind_at-time"
        dataTestElement="dropdown_task_remind_at-time"
        label="Time"
        options={timeOptions}
        onChange={(v) => handleChange('due_time', v)}
        shouldSort={false}
        clearable={false}
        className="w-1/2 pl-5"
        labelClassName="capitalize"
        disabled_prop={datePicker.touched ? !!datePicker.invalid : true}
      />
    </div>
    <div>
      <SearchableSelectField
        className="w-full"
        labelClassName="capitalize"
        label="Link To Client"
        placeholder="Search"
        options={clientOptions}
        onChange={handleClientSelectionChange}
        onInputChange={handleClientInputChange}
        inputValue={clientSearchQuery}
        value={form.client_id}
        truncateOptions={false}
        clearable
      />
      {loadingPersons && (
        <div className="mt-1 text-xs text-medium-grey" role="status" aria-live="polite">
          Loading...
        </div>
      )}
    </div>
    <div>
      <SearchableSelectField
        className="w-full"
        labelClassName="capitalize"
        label="Assign Task To"
        required
        placeholder="Search"
        options={employeeOptions}
        onChange={handleEmployeeSelectionChange}
        onInputChange={handleEmployeeInputChange}
        inputValue={employeeSearchQuery}
        value={form.assign_to}
        truncateOptions={false}
      />
      {loadingEmployees && (
        <div className="mt-1 text-xs text-medium-grey" role="status" aria-live="polite">
          Loading...
        </div>
      )}
    </div>
  </div>
);

const NewTaskModalFooter = ({
  handleClose,
  handleSubmit,
  isCreating,
  isValid,
}) => (
  <div className="gap-2 bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
    <Button
      label={isCreating ? 'Creating...' : 'Create Task'}
      data-testid="create-task"
      primary
      disabled={!isValid || isCreating}
      onClick={handleSubmit}
      className="font-regular-font"
    />
    <Button
      label="Cancel"
      data-testid="cancel-task-creation"
      secondary
      onClick={handleClose}
      className="font-regular-font"
    />
  </div>
);

const NewTaskModal = ({
  show,
  onClose,
  onSuccess,
  currentProviderId,
  currentEmployeeId,
}) => {
  const { mutateAsync: createTask, isLoading: isCreating } = useCreateTask();
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const {
    data: employeesResponse,
    isLoading: loadingEmployeesInitial,
    isFetching: loadingEmployees = false,
  } = useFind(
    'employee',
    {
      provider: currentProviderId,
      query: employeeSearchQuery,
    },
    { queryConfig: { enabled: show && !!currentProviderId, placeholderData: undefined } },
  );
  const employeeOptions = get(employeesResponse, 'data.data', []).map((e) => ({
    value: e.id,
    label: `${get(e, 'first_name', '')} ${get(e, 'last_name', '')}`.trim(),
  }));
  const [clientSearchQuery, setClientSearchQuery] = useState('');

  const {
    data: personsResponse,
    isLoading: loadingPersonsInitial,
    isFetching: loadingPersons = false,
  } = useFind(
    'people_view',
    {
      'client_relationships.provider': currentProviderId,
      query: clientSearchQuery,
    },
    { queryConfig: { enabled: show && !!currentProviderId, placeholderData: undefined } },
  );
  const clientOptions = get(personsResponse, 'data.data', []).map((p) => ({
    value: p.id,
    label: get(p, 'full_name', ''),
    secondaryText: formatDateOfBirth(get(p, 'date_of_birth')) ?
      `DOB: ${formatDateOfBirth(get(p, 'date_of_birth'))}` : '',
  }));
  const isModalLoading = (
    (!employeeSearchQuery && loadingEmployeesInitial) ||
    (!clientSearchQuery && loadingPersonsInitial)
  );

  const datePickerRef = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [datePicker, setDatePicker] = useState({ touched: false, invalid: false });
  const [nameState, setNameState] = useState({ touched: false, invalid: false, error: undefined });
  const [timeOptions, setTimeOptions] = useState([]);

  const handleDateChange = (value) => {
    if (!value) return;
    const errors = datePickerRef.current?.validate(value);
    const hasError = errors && errors.length > 0;
    setDatePicker({ touched: true, invalid: hasError });
    if (!hasError) {
      setForm((prev) => ({ ...prev, due_date: value, due_time: '' }));
      setTimeOptions(timeOfDayOptions({ increment: 60, date: new Date(value) }));
    } else {
      setForm((prev) => ({ ...prev, due_date: null, due_time: '' }));
      setTimeOptions([]);
    }
  };

  const handleNameBlur = (e) => {
    const { value } = e.target;
    const error = validations.isRequired(value);
    const hasError = !value || (error && error.length > 0);
    setNameState({ touched: true, invalid: hasError, error });
  };

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const handleClientInputChange = (value) => {
    setClientSearchQuery(value);
    setForm((prev) => ({ ...prev, client_id: '' }));
  };
  const handleClientSelectionChange = (option) => {
    setClientSearchQuery(option?.label ?? '');
    handleChange('client_id', option?.value ?? '');
  };
  const handleEmployeeInputChange = (value) => {
    setEmployeeSearchQuery(value);
    setForm((prev) => ({ ...prev, assign_to: '' }));
  };
  const handleEmployeeSelectionChange = (option) => {
    setEmployeeSearchQuery(option?.label ?? '');
    handleChange('assign_to', option?.value ?? '');
  };

  const isValid = () => Boolean(
    form.name.trim() &&
    form.details.trim() &&
    form.due_date &&
    form.assign_to,
  );

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setClientSearchQuery('');
    setEmployeeSearchQuery('');
    setDatePicker({ touched: false, invalid: false });
    setNameState({ touched: false, invalid: false, error: undefined });
    setTimeOptions([]);
    onClose();
  };

  const handleSubmit = async () => {
    if (!isValid()) return;
    try {
      const date = new Date(form.due_date);
      const totalMinutes = getDueTimeInMinutes({ date, dueTime: form.due_time });
      date.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
      const payload = {
        name: form.name,
        description: form.details,
        due_date: date.toISOString(),
        assignee_employee: form.assign_to,
        assignee_provider: currentProviderId,
        subject_type: 'Employee',
        subject_id: currentEmployeeId,
        ...(form.client_id ? { person: form.client_id } : {}),
      };
      await createTask(payload);
      notifyTaskCreatedSuccess();
      callOrLog(() => window.eventTracker(TASKS.v3CreateTaskSubmitted));
      if (onSuccess) onSuccess();
      handleClose();
    } catch (e) {
      notifyTaskCreatedError();
      console.error('Failed to create task', e); // eslint-disable-line no-console
    }
  };

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className="relative"
        style={{ zIndex: 110 }}
        onClose={() => {}}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-0"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-0"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-0"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-0"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={
                  'new-task-modal relative transform rounded-lg bg-white text-left ' +
                  'shadow-xl transition-all sm:mb-8 sm:mt-20 sm:w-full sm:max-w-lg'
                }
                data-testid="new-task-modal"
              >
                <NewTaskModalHeader onClose={handleClose} />

                <Dialog.Description
                  as="div"
                  className="px-6 py-4 bg-light-border-grey border-t border-b border-solid border-med-pale-blue"
                >
                  {isModalLoading ? (
                    <NewTaskModalLoadingState />
                  ) : (
                    <NewTaskModalFields
                      datePicker={datePicker}
                      datePickerRef={datePickerRef}
                      employeeOptions={employeeOptions}
                      employeeSearchQuery={employeeSearchQuery}
                      clientOptions={clientOptions}
                      clientSearchQuery={clientSearchQuery}
                      form={form}
                      handleChange={handleChange}
                      handleClientInputChange={handleClientInputChange}
                      handleClientSelectionChange={handleClientSelectionChange}
                      handleEmployeeInputChange={handleEmployeeInputChange}
                      handleEmployeeSelectionChange={handleEmployeeSelectionChange}
                      handleDateChange={handleDateChange}
                      handleNameBlur={handleNameBlur}
                      loadingEmployees={loadingEmployees}
                      loadingPersons={loadingPersons}
                      nameState={nameState}
                      timeOptions={timeOptions}
                    />
                  )}
                </Dialog.Description>

                <NewTaskModalFooter
                  handleClose={handleClose}
                  handleSubmit={handleSubmit}
                  isCreating={isCreating}
                  isValid={isValid()}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

NewTaskModalHeader.propTypes = {
  onClose: PropTypes.func.isRequired,
};

NewTaskModalFields.propTypes = {
  datePicker: PropTypes.shape({
    touched: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
  }).isRequired,
  datePickerRef: PropTypes.shape({
    current: PropTypes.shape({
      validate: PropTypes.func,
    }),
  }).isRequired,
  employeeOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  employeeSearchQuery: PropTypes.string.isRequired,
  clientOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    secondaryText: PropTypes.string,
  })).isRequired,
  clientSearchQuery: PropTypes.string.isRequired,
  form: PropTypes.shape({
    client_id: PropTypes.string.isRequired,
    assign_to: PropTypes.string.isRequired,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleClientInputChange: PropTypes.func.isRequired,
  handleClientSelectionChange: PropTypes.func.isRequired,
  handleEmployeeInputChange: PropTypes.func.isRequired,
  handleEmployeeSelectionChange: PropTypes.func.isRequired,
  handleDateChange: PropTypes.func.isRequired,
  handleNameBlur: PropTypes.func.isRequired,
  loadingEmployees: PropTypes.bool.isRequired,
  loadingPersons: PropTypes.bool.isRequired,
  nameState: PropTypes.shape({
    touched: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }).isRequired,
  timeOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    disabled: PropTypes.bool,
  })).isRequired,
};

NewTaskModalFooter.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isCreating: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
};

NewTaskModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  currentProviderId: PropTypes.string.isRequired,
  currentEmployeeId: PropTypes.string.isRequired,
};

NewTaskModal.defaultProps = {
  onSuccess: null,
};

export default NewTaskModal;
